import { useMutation, useQuery } from '@tanstack/react-query'
import { VehicleGroupsFilterParams, CreateVehicleGroupParams, UpdateVehicleGroupParams } from '@/types/vehicle-group'
import { useVehicleGroupsStore } from '@/store'
import { SortOrderEnum } from '@/enums'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useVehicleGroups = (params: VehicleGroupsFilterParams) => {
  const fetchVehicleGroups = useVehicleGroupsStore(state => state.fetchVehicleGroups)

  return useQuery({
    queryKey: ['vehicle-groups', params],
    queryFn: () => fetchVehicleGroups(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useVehicleGroup = (id: string) => {
  const getVehicleGroup = useVehicleGroupsStore(state => state.getVehicleGroup)

  return useQuery({
    queryKey: ['vehicle-group', id],
    queryFn: () => getVehicleGroup(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateVehicleGroup = () => {
  const createVehicleGroup = useVehicleGroupsStore(state => state.createVehicleGroup)

  return useMutation({
    mutationFn: (params: CreateVehicleGroupParams) => createVehicleGroup(params),
    onSuccess: () => {}
  })
}

export const useUpdateVehicleGroup = () => {
  const updateVehicleGroup = useVehicleGroupsStore(state => state.updateVehicleGroup)

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateVehicleGroupParams }) => updateVehicleGroup(id, params),
    onSuccess: () => {}
  })
}

export const useSortVehicleGroup = () => {
  const sortVehicleGroup = useVehicleGroupsStore(state => state.sortVehicleGroup)

  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: SortOrderEnum }) => sortVehicleGroup(id, type),
    onSuccess: () => {}
  })
}

export const useDeleteVehicleGroup = () => {
  const deleteVehicleGroup = useVehicleGroupsStore(state => state.deleteVehicleGroup)

  return useMutation({
    mutationFn: (id: string) => deleteVehicleGroup(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteVehicleGroups = () => {
  const bulkDeleteVehicleGroups = useVehicleGroupsStore(state => state.bulkDeleteVehicleGroups)
  const deselectAllVehicleGroups = useVehicleGroupsStore(state => state.deselectAllVehicleGroups)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteVehicleGroups(ids),
    onSuccess: response => {
      deselectAllVehicleGroups()
      handleBulkDeleteSuccess(response)
    }
  })
}
